import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ShieldCheck, ShieldX, ShieldAlert, Calendar, User, Award, ExternalLink } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  VALID:   { color: "var(--green)", Icon: ShieldCheck, label: "Verified & active" },
  EXPIRED: { color: "var(--amber)", Icon: ShieldAlert, label: "Verified but expired" },
  INVALID: { color: "var(--red)",   Icon: ShieldX,     label: "Signature invalid" },
};

/** Public verification page — no auth required. This is the URL recipients
 *  share with employers. Designed to render fast and look authoritative. */
export default function VerifyBadge() {
  const { badgeId } = useParams();
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/badges/${badgeId}`);
        setResult(res.data);
      } catch (e) {
        setError(e?.response?.status === 404 ? "not_found" : "fetch_failed");
      } finally {
        setLoading(false);
      }
    })();
  }, [badgeId]);

  return (
    <div className="min-h-screen bg-[var(--bg)] flex flex-col">
      <header className="border-b border-[var(--border)] px-6 py-4">
        <Link to="/" className="inline-flex items-center gap-2">
          <span className="font-display font-bold text-lg tracking-tight uppercase">
            Def<span className="text-[var(--cyan)]">Dossier</span>
          </span>
        </Link>
      </header>

      <main className="flex-1 flex items-start justify-center px-6 py-10">
        <div className="w-full max-w-2xl">
          {loading && (
            <div className="flat-card p-8 text-center font-mono text-sm text-[var(--text-2)]">
              // Verifying credential…
            </div>
          )}

          {error === "not_found" && (
            <div className="flat-card p-8 border-l-4 border-[var(--red)]" data-testid="badge-not-found">
              <div className="flex items-center gap-3 mb-2">
                <ShieldX size={28} color="var(--red)" strokeWidth={1.5} />
                <div className="overline" style={{ color: "var(--red)" }}>// CREDENTIAL_NOT_FOUND</div>
              </div>
              <h1 className="font-display text-2xl uppercase tracking-tight mb-2">No badge with this ID exists</h1>
              <p className="text-sm text-[var(--text-2)]">
                The URL may be malformed, or this credential has been revoked. Ask the recipient to
                share a fresh verification link from their wallet.
              </p>
              <p className="font-mono text-xs text-[var(--text-2)] mt-4 break-all">
                Requested ID: <span className="text-[var(--cyan)]">{badgeId}</span>
              </p>
            </div>
          )}

          {error === "fetch_failed" && (
            <div className="flat-card p-8 border-l-4 border-[var(--red)]" data-testid="badge-fetch-failed">
              <h1 className="font-display text-2xl uppercase tracking-tight mb-2">Could not reach issuer</h1>
              <p className="text-sm text-[var(--text-2)]">Please retry in a moment.</p>
            </div>
          )}

          {result && <BadgeCard result={result} />}
        </div>
      </main>

      <footer className="border-t border-[var(--border)] px-6 py-3 text-center font-mono text-[10px] tracking-widest uppercase text-[var(--text-2)]">
        Verified by {ISSUER_NAME}
      </footer>
    </div>
  );
}

const ISSUER_NAME = "DefDossier · Sentinel Verification Service";

function BadgeCard({ result }) {
  const meta = STATUS_META[result.status] || STATUS_META.INVALID;
  const Icon = meta.Icon;
  const c = result.credential || {};
  const ach = c.credentialSubject?.achievement || {};
  return (
    <div className="space-y-4" data-testid="badge-card">
      <div className="flat-card p-8 scanlines border-l-4" style={{ borderLeftColor: meta.color }}>
        <div className="flex items-center gap-4 mb-6">
          <div
            className="w-16 h-16 border-2 flex items-center justify-center shrink-0"
            style={{ borderColor: meta.color, color: meta.color }}
          >
            <Icon size={32} strokeWidth={1.5} />
          </div>
          <div>
            <div className="overline mb-1" style={{ color: meta.color }} data-testid="badge-status">
              // {result.status} · {meta.label}
            </div>
            <h1 className="font-display font-bold text-2xl sm:text-3xl uppercase tracking-tight" data-testid="badge-title">
              {result.achievement_name || ach.name || "DefDossier Credential"}
            </h1>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4 mb-6 border-y border-[var(--border)] py-4">
          <Field icon={User} label="Recipient" value={result.recipient_name} />
          <Field icon={Award} label="Issuer" value={result.issuer} />
          <Field icon={Calendar} label="Issued" value={result.issued_at ? new Date(result.issued_at).toLocaleDateString() : "—"} />
          <Field icon={Calendar} label="Expires" value={result.expires_at ? new Date(result.expires_at).toLocaleDateString() : "Never"} />
        </div>

        {result.achievement_description && (
          <div className="mb-6">
            <div className="overline mb-2">// CRITERIA</div>
            <p className="text-sm text-[var(--text)] leading-relaxed">{result.achievement_description}</p>
          </div>
        )}

        <div className="border-t border-[var(--border)] pt-4">
          <div className="overline mb-2">// CRYPTOGRAPHIC_PROOF</div>
          <div className="font-mono text-[10px] text-[var(--text-2)] space-y-1">
            <div>BADGE_ID &nbsp;<span className="text-[var(--cyan)]">{result.badge_id}</span></div>
            <div>VERIFIED &nbsp;{result.verified_at}</div>
            <div>SIGNATURE&nbsp;HS256 (issuer-key)</div>
          </div>
        </div>
      </div>

      <div className="flat-card p-4 text-center text-xs text-[var(--text-2)]">
        Looking for cyber defence training that produces verifiable, audit-ready credentials?{" "}
        <Link to="/" className="text-[var(--cyan)] hover:underline inline-flex items-center gap-1">
          Learn more <ExternalLink size={11} />
        </Link>
      </div>
    </div>
  );
}

function Field({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2">
      <Icon size={14} className="mt-0.5 shrink-0" color="var(--text-2)" strokeWidth={1.5} />
      <div>
        <div className="font-mono text-[10px] tracking-widest uppercase text-[var(--text-2)]">{label}</div>
        <div className="text-sm text-white">{value || "—"}</div>
      </div>
    </div>
  );
}
